import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BarLoader } from 'react-spinners'
import CalendarNav from './CalendarNav/CalendarNav'
import css from './SecondaryNav.module.css'
import PlaybookNav from './PlaybookNav/PlaybookNav'
import MembersNav from './MembersNav/MembersNav'
import classNames from 'classnames/bind'
import { usePageContext } from '../../../context/PageContext'
import { AppContext } from '../../../context/AppContext'
import useGymTracks from '../../../hooks/useGymTracks'
import {
  QueryObserver,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'
import { set } from 'lodash'

type Props = {
  data?: {
    activeType: string
  }
  isLoading?: boolean
  onChange?: (params: any) => void
}

const cx = classNames.bind(css)

const SecondaryNav = ({ data, onChange }: Props) => {
  const { pathname: page } = useLocation()

  const {
    state: { isLoading },
  } = usePageContext()

  const mutationState = useMutationState()

  // If queryClient has pending mutations, we will notify the user
  // that they shouldn't close the browser window.
  // If queryClient has failed mutations (that are currently retrying),
  // We will show navigation loading bar so it would make more sense
  // why we're asking not to close browser window.

  const hasPendingMutations =
    mutationState.filter((mutation) => mutation.status === 'pending').length > 0

  const hasFailedMutations =
    mutationState.reduce(
      (count, mutation) => count + mutation.failureCount,
      0
    ) > 0

  const hasRetryingMutations =
    mutationState.filter((mutation) => mutation.failureCount > 0).length > 0

  const hasPausedMutations =
    mutationState.filter((mutation) => mutation.isPaused).length > 0

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasRetryingMutations || hasPendingMutations || hasFailedMutations) {
        const message =
          'Changes you made may not be saved. Are you sure you want to leave?'
        e.preventDefault()
        e.returnValue = message
        return message
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasPendingMutations, hasRetryingMutations, hasFailedMutations])

  const getSecondaryNavContent = () => {
    switch (page) {
      case '/':
        return <CalendarNav />
      case '/playbook':
        return <PlaybookNav />
      case '/members':
        return <MembersNav />
      case '/gym-settings':
        return <h2>Gym Settings</h2>
      case '/account':
        return <h2>Account</h2>
      case '/test':
        return <h2>Test</h2>
      default:
        return <h2>Secondary Title</h2>
    }
  }

  const gymTracks = useGymTracks()
  const calendarHasTracks = page === '/' && gymTracks.length > 1

  return (
    <header
      className={cx('secondary-nav', { calendarWithTracks: calendarHasTracks })}
    >
      {getSecondaryNavContent()}
      {(isLoading ||
        hasFailedMutations ||
        hasRetryingMutations ||
        hasPausedMutations) && (
        <BarLoader
          color="var(--accent-hover)"
          height={3}
          style={{
            position: 'absolute',
            width: '100%',
            height: '4px',
            backgroundColor: 'var(--accent-darker)',
            bottom: '0px',
            overflow: 'hidden',
            marginLeft: '-28px',
          }}
        />
      )}
    </header>
  )
}

export default SecondaryNav
